import { HttpErrorResponse } from '@angular/common/http';
import { ApiError, ERROR_TYPE } from '../models/api.model';

/** Estrae `errorType` dal corpo d'errore di sso-bck, se il corpo è quello previsto. */
export function errorTypeOf(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }
  const body = error.error as ApiError | string | null;
  if (body === null || typeof body === 'string') {
    // Le eccezioni non gestite escono da StatusPages come testo "500: ...".
    return null;
  }
  return typeof body.errorType === 'string' ? body.errorType : null;
}

/** Messaggi in italiano per i casi di dominio che l'utente può correggere. */
const MESSAGES: Record<string, string> = {
  [ERROR_TYPE.usernameAlreadyTaken]: 'Questo username è già assegnato a un altro utente.',
  [ERROR_TYPE.missingMetadata]: 'Manca un dato obbligatorio: la password o l’Object ID Microsoft.',
  [ERROR_TYPE.invalidMetadata]:
    'L’Object ID Microsoft non è valido oppure appartiene già a un altro utente.',
  [ERROR_TYPE.unsupportedUpdate]:
    'I dati di questo utente arrivano da un sistema esterno: puoi cambiare solo lo stato attivo.',
  [ERROR_TYPE.accountGroupAlreadyMember]: 'L’utente ha già questo gruppo.',
  [ERROR_TYPE.unsupportedAccountType]: 'Operazione non disponibile per questo tipo di account.',
  [ERROR_TYPE.adminNotSupported]:
    "L'amministratore principale non si può eliminare, disattivare né privare dei suoi gruppi.",
  [ERROR_TYPE.groupDisplayNameAlreadyUsed]: 'Questa applicazione ha già un gruppo con questo nome.',
  [ERROR_TYPE.groupIsClientDefault]:
    'È il gruppo di default della sua applicazione: cambialo nella modifica del client prima di eliminarlo.',
  [ERROR_TYPE.invalidDefaultGroup]:
    'Il gruppo scelto non è più valido: aggiorna la lista e riprova.',
  [ERROR_TYPE.missingDefaultGroup]:
    'Per attivare subito i nuovi utenti serve un gruppo di default.',
  [ERROR_TYPE.unexpectedDefaultGroup]: 'Il gruppo di default non serve se l’accesso è bloccato.',
  [ERROR_TYPE.invalidAutoProvisionAccountType]:
    'La creazione automatica non è disponibile per questo tipo di account.',
  [ERROR_TYPE.accessRequestNotFound]:
    'La richiesta è già stata gestita, forse da un altro amministratore: aggiorna la lista.',
  [ERROR_TYPE.accessRequestInvalidGroup]:
    'Il gruppo scelto non è più valido: aggiorna la lista e riprova.',
  [ERROR_TYPE.clientDisplayNameAlreadyUsed]: 'Esiste già un client con questo nome.',
  [ERROR_TYPE.ssoNotAlterable]:
    'Il client SSO e i suoi gruppi sono di sistema: non si possono modificare né eliminare.',
  [ERROR_TYPE.invalidPublicKey]:
    'La chiave pubblica non è valida: controlla di averla copiata per intero.',
  [ERROR_TYPE.redirectAlreadyRegistered]: 'Questo indirizzo è già registrato.',
  [ERROR_TYPE.invalidUrl]: 'Indirizzo non valido.',
  [ERROR_TYPE.totpNotSupported]:
    'La verifica in due passaggi non è disponibile per questo tipo di utente.',
  [ERROR_TYPE.accountNotFound]: "L'utente non esiste più: aggiorna la lista.",
  [ERROR_TYPE.groupNotFound]: 'Il gruppo non esiste più: aggiorna la lista.',
  [ERROR_TYPE.clientNotFound]: 'Il client non esiste più: aggiorna la lista.',
  [ERROR_TYPE.clientRedirectNotFound]: 'L’indirizzo non esiste più: aggiorna la lista.',
  [ERROR_TYPE.accountGroupNotFound]: "L'assegnazione non esiste più: aggiorna la lista.",
};

/**
 * Messaggio da mostrare per un errore dell'API.
 *
 * `fallback` descrive l'operazione tentata ("Impossibile salvare l'utente."): serve
 * perché sso-bck restituisce 500 anche per errori di dominio non elencati qui, e il
 * `message` del backend è tecnico (a volte contiene dati che non vanno esposti).
 */
export function describeApiError(error: unknown, fallback: string): string {
  const errorType = errorTypeOf(error);
  if (errorType !== null && Object.hasOwn(MESSAGES, errorType)) {
    return MESSAGES[errorType] as string;
  }

  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return 'Servizio non raggiungibile. Controlla la connessione e riprova.';
    }
    if (error.status === 400) {
      return `${fallback} La richiesta è stata rifiutata: controlla i dati inseriti.`;
    }
    if (error.status === 404) {
      return `${fallback} La risorsa non esiste più.`;
    }
  }

  return `${fallback} Si è verificato un errore inatteso.`;
}
